import React, { useState, useEffect } from 'react';
import { t } from 'i18next';
import { Dialog } from '@mui/material';
import { MainButton } from '../../Buttons/MainButton';
import { UserProps } from '../../../graphql/apis/auth/mutations';
import { useBlockUser } from '../../../graphql/apis/users/mutations/hooks/useBlockUser';
import { useAppSelector } from '../../../redux/hooks';
import { colors } from '../../../theme/colors';
import { fontSize } from '../../../theme/typography';
import spacing from '../../../theme/spacing';
import {
  simpleModalContentContainerStyles,
  simpleModalActionsContainerStyles,
  simpleModalPaperDynamicSizeRootStyles,
} from '../../Modals/styles/simpleModalStyles';

interface UnblockActionButtonProps {
  user?: UserProps;
  style?: any;
  onBlockStateChange?: (isBlocked: boolean) => void;
  initialIsBlocked?: boolean;
  refetchBlockedUsers?: () => Promise<any>;
}

const UnblockActionButton = ({
  user,
  style,
  onBlockStateChange,
  initialIsBlocked = false,
  refetchBlockedUsers,
}: UnblockActionButtonProps) => {
  const currentUser = useAppSelector((state) => state.user);
  const { unblockUser, isUnblocking } = useBlockUser();

  const [showUnblockConfirm, setShowUnblockConfirm] = useState(false);
  const [isBlockedByCurrentUser, setIsBlockedByCurrentUser] =
    useState(initialIsBlocked);

  const isOwnProfile = currentUser._id === user?._id;

  useEffect(() => {
    setIsBlockedByCurrentUser(initialIsBlocked);
  }, [initialIsBlocked]);

  if (isOwnProfile || !currentUser.isLoggedIn || !user) {
    return null;
  }

  const handleUnblock = async () => {
    try {
      await unblockUser(user.username || user._id, { refetchBlockedUsers });
      setShowUnblockConfirm(false);
      setIsBlockedByCurrentUser(false);
      onBlockStateChange?.(false);
    } catch (error) {
      console.error('Failed to unblock user:', error);
    }
  };

  if (isBlockedByCurrentUser) {
    return (
      <>
        <MainButton
          onClick={() => setShowUnblockConfirm(true)}
          variant="outlined"
          size="small"
          typeStyle="secondary"
          text={t('common.unblock')}
          style={style}
        />
        <Dialog
          open={showUnblockConfirm}
          onClose={() => !isUnblocking && setShowUnblockConfirm(false)}
          sx={{
            '& .MuiPaper-root': {
              ...simpleModalPaperDynamicSizeRootStyles,
            },
          }}
        >
          <div style={simpleModalContentContainerStyles}>
            <div
              style={{
                color: colors.textPrimary,
                fontSize: fontSize.lg,
                fontWeight: '500',
                textAlign: 'center',
              }}
            >
              {t('accountSettings.unblockUserConfirmTitle', {
                username: user.username || '',
              })}
            </div>
            <div
              style={{
                color: colors.textGreyMedium,
                fontSize: fontSize.regular,
                textAlign: 'center',
                marginTop: spacing.small,
              }}
            >
              {t('accountSettings.unblockUserConfirmMessage')}
            </div>
          </div>

          <div
            style={{
              ...simpleModalActionsContainerStyles,

              padding: spacing.medium,
            }}
          >
            <MainButton
              onClick={() => setShowUnblockConfirm(false)}
              typeStyle="secondary"
              variant="outlined"
              text={t('common.cancel')}
              disabled={isUnblocking}
            />
            <MainButton
              onClick={handleUnblock}
              typeStyle="secondaryLight"
              text={t('common.unblock')}
              disabled={isUnblocking}
              style={{
                marginLeft: spacing.small,
              }}
            />
          </div>
        </Dialog>
      </>
    );
  }

  return null;
};

export default UnblockActionButton;
